import { Mail, Phone, Globe, User, Plus, Trash2, Building2, Lock } from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle, CardDescription, CardFooter } from "../ui/card";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import { Badge } from "../ui/badge";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../ui/select";

const TIMEZONES = [
  { value: "America/New_York", label: "Eastern (ET)" },
  { value: "America/Chicago", label: "Central (CT)" },
  { value: "America/Denver", label: "Mountain (MT)" },
  { value: "America/Los_Angeles", label: "Pacific (PT)" },
  { value: "America/Phoenix", label: "Arizona (AZ)" },
  { value: "America/Anchorage", label: "Alaska (AK)" },
  { value: "Pacific/Honolulu", label: "Hawaii (HI)" },
  { value: "America/Toronto", label: "Toronto (ET)" },
  { value: "Europe/London", label: "London (GMT)" },
];

const CONTACT_ROLES = [
  "Project Manager",
  "Developer",
  "Architect",
  "Marketing",
  "Finance",
  "Other"
];

function FromQuoteBadge() {
  return (
    <Badge variant="secondary" className="text-[10px] font-normal gap-1">
      <Lock className="w-2.5 h-2.5" /> from quote
    </Badge>
  );
}

export default function IntakeClientStep({ form, setForm, errors, onNext, quoteMode = false }) {
  const addExternalContact = () => {
    const contacts = form.external_contacts || [];
    setForm({
      ...form,
      external_contacts: [...contacts, { name: "", email: "", role: "" }]
    });
  };

  const removeExternalContact = (index) => {
    const contacts = [...(form.external_contacts || [])];
    contacts.splice(index, 1);
    setForm({ ...form, external_contacts: contacts });
  };

  const updateAP = (field, value) => {
    setForm({
      ...form,
      accounts_payable: { ...(form.accounts_payable || {}), [field]: value },
    });
  };

  // The client usually pays their own invoices, so one click copies them in
  // rather than making the rep type the same name and email twice.
  const apIsClient =
    !!(form.accounts_payable?.email || "").trim() &&
    (form.accounts_payable?.name || "").trim() === (form.client_name || "").trim() &&
    (form.accounts_payable?.email || "").trim().toLowerCase() === (form.client_email || "").trim().toLowerCase();

  const copyClientToAP = () => {
    setForm({
      ...form,
      accounts_payable: {
        ...(form.accounts_payable || {}),
        name: (form.client_name || "").trim(),
        email: (form.client_email || "").trim(),
        phone: (form.accounts_payable?.phone || "").trim() || form.client_phone || "",
      },
    });
  };

  const updateExternalContact = (index, field, value) => {
    const contacts = [...(form.external_contacts || [])];
    contacts[index] = { ...contacts[index], [field]: value };
    setForm({ ...form, external_contacts: contacts });
  };

  return (
    <Card>
      <CardHeader className="px-4 sm:px-6">
        <CardTitle className="flex items-center gap-2 text-lg sm:text-xl">
          <User className="w-5 h-5" />
          Client Information
        </CardTitle>
        <CardDescription className="text-sm">Enter the client's contact details</CardDescription>
      </CardHeader>
      <CardContent className="space-y-4 px-4 sm:px-6">
        <div className="space-y-1.5">
          <Label className="text-sm flex items-center gap-2">Client Full Name * {quoteMode && <FromQuoteBadge />}</Label>
          <Input
            value={form.client_name}
            onChange={e => setForm({ ...form, client_name: e.target.value })}
            placeholder="Acme Development Corp"
            readOnly={quoteMode}
            className={`h-10 sm:h-11 ${quoteMode ? "bg-muted/60 cursor-not-allowed" : ""} ${errors.client_name ? "border-red-500" : ""}`}
            autoFocus={!quoteMode}
          />
          {errors.client_name && <p className="text-xs text-red-500">{errors.client_name}</p>}
        </div>

        <div className="space-y-1.5">
          <Label className="text-sm flex items-center gap-2">Company / LLC {quoteMode && <FromQuoteBadge />}</Label>
          <div className="relative">
            <Building2 className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
            <Input
              value={form.client_entity || ""}
              onChange={e => setForm({ ...form, client_entity: e.target.value })}
              placeholder="Acme Development LLC"
              readOnly={quoteMode}
              className={`pl-10 h-10 sm:h-11 ${quoteMode ? "bg-muted/60 cursor-not-allowed" : ""}`}
            />
          </div>
          {!quoteMode && <p className="text-xs text-muted-foreground">Legal entity on the contract (optional)</p>}
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div className="space-y-1.5">
            <Label className="text-sm flex items-center gap-2">Email * {quoteMode && <FromQuoteBadge />}</Label>
            <div className="relative">
              <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
              <Input
                type="email"
                value={form.client_email}
                onChange={e => setForm({ ...form, client_email: e.target.value })}
                placeholder="contact@client.com"
                readOnly={quoteMode}
                className={`pl-10 h-10 sm:h-11 ${quoteMode ? "bg-muted/60 cursor-not-allowed" : ""} ${errors.client_email ? "border-red-500" : ""}`}
              />
            </div>
            {errors.client_email && <p className="text-xs text-red-500">{errors.client_email}</p>}
          </div>

          <div className="space-y-1.5">
            <Label className="text-sm">Phone *</Label>
            <div className="relative">
              <Phone className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
              <Input
                value={form.client_phone}
                onChange={e => setForm({ ...form, client_phone: e.target.value })}
                placeholder="(555) 123-4567"
                autoFocus={quoteMode}
                className={`pl-10 h-10 sm:h-11 ${errors.client_phone ? "border-red-500" : ""}`}
              />
            </div>
            {errors.client_phone && <p className="text-xs text-red-500">{errors.client_phone}</p>}
          </div>
        </div>
        
        <div className="space-y-1.5">
          <Label className="text-sm">Time Zone *</Label>
          <Select value={form.client_timezone} onValueChange={v => setForm({ ...form, client_timezone: v })}>
            <SelectTrigger className="h-10 sm:h-11">
              <Globe className="w-4 h-4 mr-2 text-muted-foreground" />
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {TIMEZONES.map(tz => (
                <SelectItem key={tz.value} value={tz.value}>{tz.label}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        {/* External Contacts Section */}
        <div className="pt-4 border-t">
          <div className="flex items-center justify-between mb-3">
            <Label className="text-sm font-medium">Additional Contacts (Optional)</Label>
            <Button
              type="button"
              variant="outline"
              size="sm"
              onClick={addExternalContact}
              className="h-8 text-xs"
            >
              <Plus className="w-3 h-3 mr-1" />
              Add Contact
            </Button>
          </div>
          
          {(form.external_contacts || []).length > 0 && (
            <div className="space-y-3">
              {(form.external_contacts || []).map((contact, idx) => {
                const rowError = errors?.[`external_contact_${idx}`];
                const missingName = rowError && !(contact.name || "").trim();
                const missingEmail = rowError && !missingName;
                return (
                <div key={idx} className="p-3 bg-muted/50 rounded-lg space-y-3">
                  <div className="flex items-center justify-between">
                    <span className="text-xs font-medium text-muted-foreground">Contact {idx + 1}</span>
                    <Button
                      type="button"
                      variant="ghost"
                      size="sm"
                      onClick={() => removeExternalContact(idx)}
                      className="h-6 w-6 p-0 text-muted-foreground hover:text-red-500"
                    >
                      <Trash2 className="w-3.5 h-3.5" />
                    </Button>
                  </div>
                  <div className="grid grid-cols-1 sm:grid-cols-3 gap-2">
                    <Input
                      value={contact.name}
                      onChange={e => updateExternalContact(idx, "name", e.target.value)}
                      placeholder="Name"
                      className={`h-9 text-sm ${missingName ? "border-red-500" : ""}`}
                    />
                    <Input
                      type="email"
                      value={contact.email}
                      onChange={e => updateExternalContact(idx, "email", e.target.value)}
                      placeholder="Email"
                      className={`h-9 text-sm ${missingEmail ? "border-red-500" : ""}`}
                    />
                    <Select
                      value={contact.role}
                      onValueChange={v => updateExternalContact(idx, "role", v)}
                    >
                      <SelectTrigger className="h-9 text-sm">
                        <SelectValue placeholder="Role" />
                      </SelectTrigger>
                      <SelectContent>
                        {CONTACT_ROLES.map(role => (
                          <SelectItem key={role} value={role}>{role}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  {rowError && (
                    <p className="text-xs text-red-500" data-testid={`external-contact-error-${idx}`}>
                      {rowError}
                    </p>
                  )}
                </div>
                );
              })}
            </div>
          )}
        </div>

        {/* Accounts Payable Manager — the one contact the money flow depends on.
            Every payment-due, past-due and interest notice routes here instead
            of to the primary contact, who is usually the developer or the
            marketing lead. Required to activate the project, not to submit the
            form, so a rep chasing the detail is never stuck mid-intake. */}
        <div className="space-y-3 pt-5 border-t">
          <div>
            <div className="flex items-center justify-between gap-2">
              <Label className="text-sm font-medium">
                Accounts Payable Manager <span className="text-red-500">*</span>
              </Label>
              {!apIsClient && (form.client_name || "").trim() && (
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={copyClientToAP}
                  className="h-8 text-xs"
                  data-testid="ap-same-as-client"
                >
                  Same as client
                </Button>
              )}
            </div>
            <p className="text-xs text-muted-foreground mt-0.5">
              Who we invoice. Every payment notice goes to this person, with the primary
              contact copied. Required before the project can be activated.
            </p>
            {quoteMode && apIsClient && (
              <p className="text-xs text-muted-foreground mt-1" data-testid="ap-from-quote-note">
                Filled in from the quote's client. If someone else pays the invoices
                (a bookkeeper, an AP desk), change it here.
              </p>
            )}
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
            <div>
              <Input
                value={form.accounts_payable?.name || ""}
                onChange={(e) => updateAP("name", e.target.value)}
                placeholder="Name *"
                data-testid="input-ap-name"
                className={`h-9 text-sm ${errors?.ap_name ? "border-red-500" : ""}`}
              />
              {errors?.ap_name && (
                <p className="text-xs text-red-500 mt-1" data-testid="ap-name-error">
                  {errors.ap_name}
                </p>
              )}
            </div>
            <div>
              <Input
                type="email"
                value={form.accounts_payable?.email || ""}
                onChange={(e) => updateAP("email", e.target.value)}
                placeholder="Email *"
                data-testid="input-ap-email"
                className={`h-9 text-sm ${errors?.ap_email ? "border-red-500" : ""}`}
              />
              {errors?.ap_email && (
                <p className="text-xs text-red-500 mt-1" data-testid="ap-email-error">
                  {errors.ap_email}
                </p>
              )}
            </div>
            <Input
              value={form.accounts_payable?.title || ""}
              onChange={(e) => updateAP("title", e.target.value)}
              placeholder="Title"
              data-testid="input-ap-title"
              className="h-9 text-sm"
            />
            <Input
              value={form.accounts_payable?.phone || ""}
              onChange={(e) => updateAP("phone", e.target.value)}
              placeholder="Phone"
              data-testid="input-ap-phone"
              className="h-9 text-sm"
            />
          </div>
        </div>
      </CardContent>
      <CardFooter className="flex justify-end px-4 sm:px-6 pb-4 sm:pb-6">
        <Button onClick={onNext} className="w-full sm:w-auto h-10 sm:h-11">
          Next: Project Details
        </Button>
      </CardFooter>
    </Card>
  );
}
